import { useCallback, useEffect, useRef, useState } from 'react';

interface UseThumbsScrollParams {
  filesLength: number;
  isSmallViewVersion?: boolean;
  isThumbsTopPosition?: boolean;
}

export interface UseThumbsScrollReturn {
  thumbsRef: React.RefObject<HTMLDivElement | null>;
  isScrollable: boolean;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  updateScrollState: () => void;
  scrollThumbs: (direction: 'left' | 'right') => void;
}

const SCROLL_AMOUNT = 250;

export const useThumbsScroll = ({
  filesLength,
  isSmallViewVersion,
  isThumbsTopPosition,
}: UseThumbsScrollParams): UseThumbsScrollReturn => {
  const thumbsRef = useRef<HTMLDivElement | null>(null);

  const [isScrollable, setIsScrollable] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = useCallback(() => {
    if (thumbsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = thumbsRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1);
      setIsScrollable(scrollWidth > clientWidth);
    }
  }, []);

  useEffect(() => {
    updateScrollState();
    const timeout = setTimeout(updateScrollState, 150);
    return () => clearTimeout(timeout);
  }, [filesLength, updateScrollState, isSmallViewVersion, isThumbsTopPosition]);

  const scrollThumbs = useCallback((direction: 'left' | 'right') => {
    if (thumbsRef.current) {
      thumbsRef.current.scrollBy({
        left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
        behavior: 'smooth',
      });
    }
  }, []);

  return {
    thumbsRef,
    isScrollable,
    canScrollLeft,
    canScrollRight,
    updateScrollState,
    scrollThumbs,
  };
};
